import { getLocalVariable, setLocalVariable } from '@services/storage';

import { DeliveryModeMapped } from '@app-types/delivery';
import { FaiProduct } from '@app-types/fai';
import { CartType, Connectivity, JOURNEYS, LightOptionType, OptionType, PlanType } from '@app-types/product';
import { Consent } from '@app-types/user';

import { v4 as uuid } from 'uuid';
import { create } from 'zustand';

type cartStoreType = {
    selectedPlan: PlanType | null;
    setSelectedPlan: (p: PlanType | null) => void;
    selectedOption: LightOptionType | null;
    setSelectedOption: (option: LightOptionType | null) => void;
    selectedFaiProduct: FaiProduct | null;
    setSelectedFaiProduct: (faiProduct: FaiProduct | null) => void;
    byouCart: null | CartType;
    addToCart: ({ product, options }: { product: PlanType; options: OptionType[] }) => void;
    updateCart: (cartItems: Partial<CartType>) => void;
    updateCartOptions: (option: OptionType) => void;
    addDeliveryMode: (deliveryMode: DeliveryModeMapped) => void;
    removeDeliveryMode: () => void;
    selectedFaiOption: string | null;
    setSelectedFaiOption: (optionId: string) => void;
    temporarilySelectedPlan: PlanType | null;
    setTemporarilySelectedPlan: (plan: PlanType | null) => void;
    selectedNetwork: Connectivity;
    setSelectedNetwork: (network: Connectivity) => void;
    temporarilySelectedFaiProduct: FaiProduct | null;
    setTemporarilySelectedFaiProduct: (faiProduct: FaiProduct | null) => void;
    otisConfigurator: { value: JOURNEYS };
    setOtisConfigurator: (newJourney: JOURNEYS) => void;
    consent: Consent | null;
    setConsent: (consentData: Consent) => void;
};
const CART_STORAGE_KEY = 'cart';
const CONSENT_KEY = 'consent';

export const cartStore = create<cartStoreType>((set) => ({
    selectedPlan: null,
    setSelectedPlan: (product: PlanType | null) =>
        set(() => ({
            selectedPlan: product,
        })),
    selectedOption: null,
    setSelectedOption: (option) =>
        set(() => ({
            selectedOption: option,
        })),
    selectedFaiProduct: null,
    setSelectedFaiProduct: (faiProduct) =>
        set(() => ({
            selectedFaiProduct: faiProduct,
        })),
    byouCart: JSON.parse(getLocalVariable(CART_STORAGE_KEY) || '{}'),
    addToCart: ({ product, options }) =>
        set(() => {
            const tCart: CartType = {
                gencode: product.gencode,
                product: { ...product },
                total: 0,
                itemsCount: 1,
                options,
                tempId: uuid(),
            };
            tCart.total = calculateTotal(tCart);

            setLocalVariable(CART_STORAGE_KEY, JSON.stringify(tCart));

            return {
                byouCart: tCart,
            };
        }),
    addDeliveryMode: (deliveryMode) => {
        set((state) => {
            const updatedCart = {
                byouCart: {
                    ...(state.byouCart as CartType),
                    deliveryMode,
                },
            };
            setLocalVariable(CART_STORAGE_KEY, JSON.stringify(updatedCart.byouCart));

            return updatedCart;
        });
    },
    removeDeliveryMode: () => {
        set((state) => {
            if (state.byouCart?.deliveryMode) {
                delete state.byouCart?.deliveryMode;
            }

            const updatedCart = {
                byouCart: {
                    ...(state.byouCart as CartType),
                },
            };
            setLocalVariable(CART_STORAGE_KEY, JSON.stringify(updatedCart.byouCart));

            return updatedCart;
        });
    },
    updateCart: (cartItems) =>
        set((state) => {
            const updatedCart = { ...(state.byouCart as CartType), ...cartItems };
            setLocalVariable(CART_STORAGE_KEY, JSON.stringify(updatedCart)); // Save to local storage
            return { byouCart: updatedCart };
        }),
    updateCartOptions: (option) => {
        set((state) => {
            const options = state.byouCart?.options ?? [];
            const findOptionIndex = options.findIndex((o) => o.gencode === option.gencode);
            if (findOptionIndex >= 0) {
                options.splice(findOptionIndex, 1);
            } else {
                options.push(option);
            }
            const updatedCart = {
                byouCart: {
                    ...(state.byouCart as CartType),
                    options,
                },
            };
            updatedCart.byouCart.total = calculateTotal(updatedCart.byouCart);
            setLocalVariable(CART_STORAGE_KEY, JSON.stringify(updatedCart.byouCart));

            return updatedCart;
        });
    },
    selectedFaiOption: null,
    setSelectedFaiOption: (optionId) => {
        set(() => ({
            selectedFaiOption: optionId,
        }));
    },
    temporarilySelectedPlan: null,
    setTemporarilySelectedPlan: (plan) => {
        set(() => ({
            temporarilySelectedPlan: plan,
        }));
    },
    selectedNetwork: Connectivity.FOUR_G,
    setSelectedNetwork: (network) => {
        set(() => ({
            selectedNetwork: network,
        }));
    },
    temporarilySelectedFaiProduct: null,
    setTemporarilySelectedFaiProduct: (temporaryFaiProduct) => {
        set(() => ({
            temporarilySelectedFaiProduct: temporaryFaiProduct,
        }));
    },
    otisConfigurator: { value: JOURNEYS.BYOU },
    setOtisConfigurator: (newJourney) => {
        set(() => ({
            otisConfigurator: { value: newJourney },
        }));
    },
    consent: JSON.parse(getLocalVariable(CONSENT_KEY) || '{}'),
    setConsent: (consentData) =>
        set((state) => {
            const updatedConcent = { ...state.consent, ...consentData };
            setLocalVariable(CONSENT_KEY, JSON.stringify(updatedConcent));
            return { consent: updatedConcent };
        }),
}));

const calculateTotal = (cart: CartType): number => {
    if (!cart.product) return 0;

    return cart.options.reduce((acc, o) => acc + o.price, cart.product.prices.final);
};
